import { FeatureOnboardingType } from "@redotech/redo-onboarding-common/user-onboarding";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * `?onboarding-preview=<type>` forces a card on screen regardless of
 * eligibility. Accepts enum keys (`MANAGED_RETURNS`) or values.
 */
export function useOnboardingPreviewType(): FeatureOnboardingType | null {
  const { search } = useLocation();
  return useMemo(() => parseOnboardingPreviewType(search), [search]);
}

export function parseOnboardingPreviewType(
  search: string,
): FeatureOnboardingType | null {
  const raw = new URLSearchParams(search).get("onboarding-preview");
  if (!raw) {
    return null;
  }
  const enumObj = FeatureOnboardingType as Record<string, string>;
  if (raw in enumObj) {
    return enumObj[raw] as FeatureOnboardingType;
  }
  const valueSet = new Set<string>(Object.values(enumObj));
  return valueSet.has(raw) ? (raw as FeatureOnboardingType) : null;
}

/**
 * A previewed card renders and reports, but must not write onboarding state:
 * a dismissal starts a cooldown and a completion suppresses the popup for
 * good, neither of which a preview should inflict on a real account.
 */
export function shouldPersistOnboardingState({
  previewType,
  type,
}: {
  previewType: FeatureOnboardingType | null;
  type: FeatureOnboardingType;
}): boolean {
  return previewType === null || type !== previewType;
}

/**
 * Whether a debounced pick may still open. Another surface, such as the credit
 * incentive modal, can take the session while it waits; a preview always opens.
 */
export function shouldShowSelectedOnboarding({
  previewType,
  type,
  shownThisSession,
}: {
  previewType: FeatureOnboardingType | null;
  type: FeatureOnboardingType;
  shownThisSession: boolean;
}): boolean {
  if (type === FeatureOnboardingType.DISABLED) {
    return false;
  }
  return (
    !shownThisSession || !shouldPersistOnboardingState({ previewType, type })
  );
}
